/**
 * Design Sets - 10 distinct visual treatment systems
 *
 * Each design set controls the overall visual style (layout, depth, animations,
 * hero treatment, card styles) independent of the color palette (design variants).
 *
 * The design set is randomly assigned per page visit and applied via CSS custom
 * properties on the root layout element.
 */

const designSets = {
    // 1 - Classic: Original clean, flat design
    1: {
        id: 1,
        name: 'classic',
        label: 'Classic',
        hero: {
            minHeight: '280px',
            overlayOpacity: '0',
            overlayStyle: 'none',
            textShadow: 'none',
            glowIntensity: '0',
            titleWeight: '700',
            titleScale: '1',
            bgMode: 'color', // color | image | gradient
        },
        cards: {
            shadow: '0 1px 3px rgba(0,0,0,0.06)',
            shadowHover: '0 4px 12px rgba(0,0,0,0.1)',
            hoverTransform: 'translateY(-2px)',
            borderWidth: '1px',
            glassBlur: '0px',
            glassOpacity: '1',
        },
        sections: {
            gap: '48px',
            innerGap: '24px',
            skewAngle: '0deg',
        },
        animations: {
            speed: '0.2s',
            easing: 'ease',
            hoverScale: '1',
            depth: 'none', // none | subtle | moderate | deep | extreme
        },
        typography: {
            heroTitleSize: 'clamp(28px, 4vw, 42px)',
            sectionTitleSize: 'clamp(24px, 3vw, 36px)',
            bodySize: '15px',
            lineHeight: '1.7',
        },
    },

    // 2 - Immersive 3D: Full-bleed bg images, 3D card depth, glassmorphism
    2: {
        id: 2,
        name: 'immersive3d',
        label: 'Immersive 3D',
        hero: {
            minHeight: '480px',
            overlayOpacity: '0.6',
            overlayStyle: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.7) 100%)',
            textShadow: '0 2px 20px rgba(0,0,0,0.3)',
            glowIntensity: '0.25',
            titleWeight: '800',
            titleScale: '1.15',
            bgMode: 'image',
        },
        cards: {
            shadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.06), 0 12px 40px rgba(0,0,0,0.08)',
            shadowHover: '0 2px 4px rgba(0,0,0,0.04), 0 8px 20px rgba(0,0,0,0.08), 0 24px 56px rgba(0,0,0,0.12)',
            hoverTransform: 'translateY(-6px)',
            borderWidth: '0px',
            glassBlur: '0px',
            glassOpacity: '1',
        },
        sections: {
            gap: '64px',
            innerGap: '32px',
            skewAngle: '0deg',
        },
        animations: {
            speed: '0.25s',
            easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            hoverScale: '1.01',
            depth: 'deep',
        },
        typography: {
            heroTitleSize: 'clamp(32px, 5vw, 56px)',
            sectionTitleSize: 'clamp(28px, 4vw, 44px)',
            bodySize: '16px',
            lineHeight: '1.7',
        },
    },

    // 3 - Glassmorphism: Frosted glass cards, translucent layers
    3: {
        id: 3,
        name: 'glassmorphism',
        label: 'Glassmorphism',
        hero: {
            minHeight: '440px',
            overlayOpacity: '0.4',
            overlayStyle: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.55) 100%)',
            textShadow: '0 1px 12px rgba(0,0,0,0.25)',
            glowIntensity: '0.3',
            titleWeight: '700',
            titleScale: '1.1',
            bgMode: 'image',
        },
        cards: {
            shadow: '0 1px 2px rgba(0,0,0,0.03), 0 8px 32px rgba(0,0,0,0.06)',
            shadowHover: '0 2px 4px rgba(0,0,0,0.04), 0 16px 48px rgba(0,0,0,0.1)',
            hoverTransform: 'translateY(-4px)',
            borderWidth: '1px',
            glassBlur: '16px',
            glassOpacity: '0.75',
        },
        sections: {
            gap: '56px',
            innerGap: '28px',
            skewAngle: '0deg',
        },
        animations: {
            speed: '0.25s',
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            hoverScale: '1.01',
            depth: 'moderate',
        },
        typography: {
            heroTitleSize: 'clamp(30px, 4.5vw, 50px)',
            sectionTitleSize: 'clamp(26px, 3.5vw, 40px)',
            bodySize: '15px',
            lineHeight: '1.7',
        },
    },

    // 4 - Neon Depth: Dark backgrounds, vivid neon glow accents
    4: {
        id: 4,
        name: 'neonDepth',
        label: 'Neon Depth',
        hero: {
            minHeight: '500px',
            overlayOpacity: '0.7',
            overlayStyle: 'linear-gradient(180deg, rgba(10,10,30,0.8) 0%, rgba(10,10,30,0.5) 50%, rgba(10,10,30,0.85) 100%)',
            textShadow: '0 0 30px rgba(251,144,125,0.4), 0 2px 10px rgba(0,0,0,0.5)',
            glowIntensity: '0.5',
            titleWeight: '800',
            titleScale: '1.2',
            bgMode: 'image',
        },
        cards: {
            shadow: '0 0 1px rgba(251,144,125,0.3), 0 1px 3px rgba(0,0,0,0.1), 0 4px 24px rgba(0,0,0,0.2)',
            shadowHover: '0 0 16px rgba(251,144,125,0.18), 0 2px 6px rgba(0,0,0,0.12), 0 8px 40px rgba(0,0,0,0.28)',
            hoverTransform: 'translateY(-5px)',
            borderWidth: '1px',
            glassBlur: '8px',
            glassOpacity: '0.9',
        },
        sections: {
            gap: '60px',
            innerGap: '30px',
            skewAngle: '0deg',
        },
        animations: {
            speed: '0.25s',
            easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            hoverScale: '1.01',
            depth: 'deep',
        },
        typography: {
            heroTitleSize: 'clamp(34px, 5.5vw, 60px)',
            sectionTitleSize: 'clamp(28px, 4vw, 46px)',
            bodySize: '16px',
            lineHeight: '1.7',
        },
    },

    // 5 - Layered Parallax: Overlapping sections with depth
    5: {
        id: 5,
        name: 'layeredParallax',
        label: 'Layered Parallax',
        hero: {
            minHeight: '520px',
            overlayOpacity: '0.5',
            overlayStyle: 'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0.6) 100%)',
            textShadow: '0 3px 16px rgba(0,0,0,0.35)',
            glowIntensity: '0.2',
            titleWeight: '800',
            titleScale: '1.15',
            bgMode: 'image',
        },
        cards: {
            shadow: '0 1px 2px rgba(0,0,0,0.04), 0 6px 20px rgba(0,0,0,0.06), 0 20px 50px rgba(0,0,0,0.05)',
            shadowHover: '0 2px 4px rgba(0,0,0,0.05), 0 12px 30px rgba(0,0,0,0.08), 0 30px 60px rgba(0,0,0,0.07)',
            hoverTransform: 'translateY(-6px)',
            borderWidth: '0px',
            glassBlur: '0px',
            glassOpacity: '1',
        },
        sections: {
            gap: '0px',
            innerGap: '32px',
            skewAngle: '-2deg',
        },
        animations: {
            speed: '0.3s',
            easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            hoverScale: '1.02',
            depth: 'extreme',
        },
        typography: {
            heroTitleSize: 'clamp(32px, 5vw, 54px)',
            sectionTitleSize: 'clamp(28px, 4vw, 42px)',
            bodySize: '16px',
            lineHeight: '1.7',
        },
    },

    // 6 - Minimal Elevation: Clean with refined 3D shadows
    6: {
        id: 6,
        name: 'minimalElevation',
        label: 'Minimal Elevation',
        hero: {
            minHeight: '360px',
            overlayOpacity: '0.45',
            overlayStyle: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.55) 100%)',
            textShadow: '0 1px 8px rgba(0,0,0,0.2)',
            glowIntensity: '0.08',
            titleWeight: '600',
            titleScale: '1',
            bgMode: 'image',
        },
        cards: {
            shadow: '0 1px 2px rgba(0,0,0,0.03), 0 2px 6px rgba(0,0,0,0.03), 0 4px 16px rgba(0,0,0,0.04)',
            shadowHover: '0 1px 3px rgba(0,0,0,0.04), 0 4px 10px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.06)',
            hoverTransform: 'translateY(-3px)',
            borderWidth: '1px',
            glassBlur: '0px',
            glassOpacity: '1',
        },
        sections: {
            gap: '48px',
            innerGap: '24px',
            skewAngle: '0deg',
        },
        animations: {
            speed: '0.2s',
            easing: 'ease-out',
            hoverScale: '1',
            depth: 'subtle',
        },
        typography: {
            heroTitleSize: 'clamp(28px, 4vw, 44px)',
            sectionTitleSize: 'clamp(24px, 3vw, 36px)',
            bodySize: '15px',
            lineHeight: '1.65',
        },
    },

    // 7 - Bold Editorial: Oversized typography, dramatic spacing
    7: {
        id: 7,
        name: 'boldEditorial',
        label: 'Bold Editorial',
        hero: {
            minHeight: '560px',
            overlayOpacity: '0.55',
            overlayStyle: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.65) 100%)',
            textShadow: '0 4px 24px rgba(0,0,0,0.4)',
            glowIntensity: '0.15',
            titleWeight: '900',
            titleScale: '1.3',
            bgMode: 'image',
        },
        cards: {
            shadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.05), 0 8px 32px rgba(0,0,0,0.06)',
            shadowHover: '0 2px 6px rgba(0,0,0,0.05), 0 8px 20px rgba(0,0,0,0.07), 0 16px 48px rgba(0,0,0,0.1)',
            hoverTransform: 'translateY(-5px)',
            borderWidth: '0px',
            glassBlur: '0px',
            glassOpacity: '1',
        },
        sections: {
            gap: '80px',
            innerGap: '40px',
            skewAngle: '0deg',
        },
        animations: {
            speed: '0.25s',
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            hoverScale: '1.01',
            depth: 'moderate',
        },
        typography: {
            heroTitleSize: 'clamp(40px, 7vw, 72px)',
            sectionTitleSize: 'clamp(32px, 5vw, 52px)',
            bodySize: '17px',
            lineHeight: '1.75',
        },
    },

    // 8 - Gradient Flow: Flowing gradients, smooth color transitions
    8: {
        id: 8,
        name: 'gradientFlow',
        label: 'Gradient Flow',
        hero: {
            minHeight: '460px',
            overlayOpacity: '0.5',
            overlayStyle: 'linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.6) 100%)',
            textShadow: '0 2px 16px rgba(0,0,0,0.3)',
            glowIntensity: '0.35',
            titleWeight: '700',
            titleScale: '1.1',
            bgMode: 'image',
        },
        cards: {
            shadow: '0 1px 2px rgba(0,0,0,0.03), 0 4px 16px rgba(0,0,0,0.05), 0 12px 36px rgba(0,0,0,0.06)',
            shadowHover: '0 2px 4px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.07), 0 20px 52px rgba(0,0,0,0.09)',
            hoverTransform: 'translateY(-5px)',
            borderWidth: '0px',
            glassBlur: '0px',
            glassOpacity: '1',
        },
        sections: {
            gap: '56px',
            innerGap: '28px',
            skewAngle: '0deg',
        },
        animations: {
            speed: '0.25s',
            easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            hoverScale: '1.01',
            depth: 'moderate',
        },
        typography: {
            heroTitleSize: 'clamp(30px, 4.5vw, 52px)',
            sectionTitleSize: 'clamp(26px, 3.5vw, 42px)',
            bodySize: '16px',
            lineHeight: '1.7',
        },
    },

    // 9 - Card Stack: Overlapping stacked cards with strong depth
    9: {
        id: 9,
        name: 'cardStack',
        label: 'Card Stack',
        hero: {
            minHeight: '400px',
            overlayOpacity: '0.5',
            overlayStyle: 'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.6) 100%)',
            textShadow: '0 2px 12px rgba(0,0,0,0.3)',
            glowIntensity: '0.2',
            titleWeight: '700',
            titleScale: '1.05',
            bgMode: 'image',
        },
        cards: {
            shadow: '0 1px 3px rgba(0,0,0,0.05), 0 6px 16px rgba(0,0,0,0.07), 0 20px 48px rgba(0,0,0,0.09)',
            shadowHover: '0 2px 6px rgba(0,0,0,0.06), 0 12px 28px rgba(0,0,0,0.1), 0 32px 64px rgba(0,0,0,0.13)',
            hoverTransform: 'translateY(-8px)',
            borderWidth: '0px',
            glassBlur: '0px',
            glassOpacity: '1',
        },
        sections: {
            gap: '48px',
            innerGap: '24px',
            skewAngle: '0deg',
        },
        animations: {
            speed: '0.25s',
            easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            hoverScale: '1.02',
            depth: 'extreme',
        },
        typography: {
            heroTitleSize: 'clamp(28px, 4.5vw, 48px)',
            sectionTitleSize: 'clamp(24px, 3.5vw, 40px)',
            bodySize: '15px',
            lineHeight: '1.65',
        },
    },

    // 10 - Cinematic: Full-bleed images, widescreen ratios, film grain feel
    10: {
        id: 10,
        name: 'cinematic',
        label: 'Cinematic',
        hero: {
            minHeight: '600px',
            overlayOpacity: '0.45',
            overlayStyle: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.7) 100%)',
            textShadow: '0 3px 20px rgba(0,0,0,0.5)',
            glowIntensity: '0.12',
            titleWeight: '800',
            titleScale: '1.25',
            bgMode: 'image',
        },
        cards: {
            shadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.06), 0 10px 30px rgba(0,0,0,0.08)',
            shadowHover: '0 2px 4px rgba(0,0,0,0.05), 0 8px 20px rgba(0,0,0,0.08), 0 20px 48px rgba(0,0,0,0.11)',
            hoverTransform: 'translateY(-4px)',
            borderWidth: '0px',
            glassBlur: '0px',
            glassOpacity: '1',
        },
        sections: {
            gap: '72px',
            innerGap: '36px',
            skewAngle: '0deg',
        },
        animations: {
            speed: '0.3s',
            easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            hoverScale: '1.01',
            depth: 'deep',
        },
        typography: {
            heroTitleSize: 'clamp(36px, 6vw, 64px)',
            sectionTitleSize: 'clamp(30px, 4.5vw, 48px)',
            bodySize: '17px',
            lineHeight: '1.75',
        },
    },
};

/**
 * Get a design set by ID
 */
export function getDesignSet(id) {
    return designSets[id] || designSets[1];
}

/**
 * Pick a random design set ID (1-10)
 */
export function randomDesignSetId() {
    return Math.floor(Math.random() * 10) + 1;
}

/**
 * Convert a design set into CSS custom properties for injection
 */
export function designSetToCssVars(ds) {
    if (!ds) return {};
    return {
        '--ds-hero-min-height': ds.hero.minHeight,
        '--ds-hero-overlay-opacity': ds.hero.overlayOpacity,
        '--ds-hero-overlay-style': ds.hero.overlayStyle,
        '--ds-hero-text-shadow': ds.hero.textShadow,
        '--ds-hero-glow-intensity': ds.hero.glowIntensity,
        '--ds-hero-title-weight': ds.hero.titleWeight,
        '--ds-hero-title-scale': ds.hero.titleScale,
        '--ds-hero-bg-mode': ds.hero.bgMode,
        '--ds-card-shadow': ds.cards.shadow,
        '--ds-card-shadow-hover': ds.cards.shadowHover,
        '--ds-card-hover-transform': ds.cards.hoverTransform,
        '--ds-card-border-width': ds.cards.borderWidth,
        '--ds-card-glass-blur': ds.cards.glassBlur,
        '--ds-card-glass-opacity': ds.cards.glassOpacity,
        '--ds-section-gap': ds.sections.gap,
        '--ds-section-inner-gap': ds.sections.innerGap,
        '--ds-section-skew': ds.sections.skewAngle,
        '--ds-anim-speed': ds.animations.speed,
        '--ds-anim-easing': ds.animations.easing,
        '--ds-anim-hover-scale': ds.animations.hoverScale,
        '--ds-anim-depth': ds.animations.depth,
        '--ds-hero-title-size': ds.typography.heroTitleSize,
        '--ds-section-title-size': ds.typography.sectionTitleSize,
        '--ds-body-size': ds.typography.bodySize,
        '--ds-body-line-height': ds.typography.lineHeight,
    };
}

/**
 * Check if the design set uses background images for hero
 */
export function heroUsesImage(ds) {
    return ds && ds.hero.bgMode === 'image';
}

export default designSets;
